import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { TERMS_VERSION } from '@smbsuite/legal';
import { tenantApi } from '../../api/endpoints';
import { useI18n } from '../../i18n';
import { Dialog, errorMessage } from '../../ui';
import { useCurrentCompany } from '../companies/hooks';

/** "Later" for the terms in force, until the browser tab is closed: asked again on the next sign-in. */
export const TERMS_LATER_KEY = 'teamfolio.termsLater';

function laterChosen(): boolean {
  try {
    return (sessionStorage.getItem(TERMS_LATER_KEY) ?? '') >= TERMS_VERSION;
  } catch {
    return false;
  }
}

/**
 * An owner of a company that has not accepted the terms of service in force is asked to (TF-256): a
 * company registered before they existed, made by the console for a customer, or whose terms changed.
 * Accepting is recorded with the company; Later asks again on the next sign-in. A showcase is not asked.
 */
export function TermsPrompt() {
  const { t } = useI18n();
  const { company } = useCurrentCompany();
  const queryClient = useQueryClient();
  const [later, setLater] = useState(laterChosen);
  const accept = useMutation({
    mutationFn: () => tenantApi.acceptTerms(company!.id, TERMS_VERSION),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['companies'] }),
  });
  const asked = Boolean(company && company.role === 'owner' && !company.showcase && company.termsVersion !== TERMS_VERSION);
  if (!asked || later || accept.isSuccess) return null;
  const postpone = () => {
    setLater(true);
    try {
      sessionStorage.setItem(TERMS_LATER_KEY, TERMS_VERSION);
    } catch {
      // Asked again on the next page load.
    }
  };
  const changed = Boolean(company?.termsVersion);
  return (
    <Dialog open onOpenChange={(open) => !open && postpone()} title={changed ? t('terms.changedTitle') : t('terms.acceptTitle')} icon="verified_user">
      <p>{t(changed ? 'terms.changedText' : 'terms.acceptText', { company: company?.name ?? '' })}</p>
      <ul className="legal-links">
        <li>
          <Link to="/legal/terms" target="_blank" rel="noopener">
            {t('legal.terms')}
          </Link>
        </li>
        <li>
          <Link to="/legal/dpa" target="_blank" rel="noopener">
            {t('legal.dpa')}
          </Link>
        </li>
        <li>
          <Link to="/privacy" target="_blank" rel="noopener">
            {t('legal.privacy')}
          </Link>
        </li>
      </ul>
      {accept.isError && <p role="alert">{errorMessage(accept.error, t('common.somethingWrong'))}</p>}
      <div className="dialog-foot">
        <button type="button" className="btn btn-text" onClick={postpone}>
          {t('terms.later')}
        </button>
        <button type="button" className="btn btn-filled" disabled={accept.isPending} onClick={() => accept.mutate()}>
          {t('terms.accept')}
        </button>
      </div>
    </Dialog>
  );
}
